import React, { useState, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, Container, Stack } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";

import { getRuta } from "../../../helpers/Helpers";
import { getToken } from "../../../api/Account";
import { getRolListAsync } from "../../../api/Rols";
import { addUserAsync } from "../../../api/Users";
import { EstadoError, PageHeader, useAviso } from "../../../components/base";
import {
  CargandoFormulario,
  llevarAlPrimerError,
  useCargaLocal,
  useEnvio,
  useEscritura,
} from "../comunes";
import FormularioUsuario, {
  DATOS_VACIOS,
  datosParaServidor,
  useJefes,
  validarUsuario,
} from "./FormularioUsuario";

const AddUser = () => {
  const token = getToken();
  let ruta = getRuta();
  let navigate = useNavigate();
  const aviso = useAviso();
  const escribir = useEscritura();
  const { guardando, enviar } = useEnvio();
  const formulario = useRef(null);

  const [datos, setDatos] = useState(DATOS_VACIOS);
  const [roles, setRoles] = useState([]);
  const { jefes, cargandoJefes, errorJefes, cargarJefes } = useJefes(token);

  // Los errores salen junto a cada campo una vez que se intentó guardar, y se corrigen en vivo.
  const [intentado, setIntentado] = useState(false);
  const errores = intentado ? validarUsuario(datos, true) : {};

  const { cargando, error, cargar } = useCargaLocal(() => getRolListAsync(token));

  const getRoles = async () => {
    const result = await cargar();
    if (result) setRoles(result.data.result);
  };

  useEffect(() => {
    getRoles();
  }, []);

  const onCampo = (campo, valor) => setDatos((actual) => ({ ...actual, [campo]: valor }));

  const onRol = (idRol) => {
    setDatos((actual) => ({ ...actual, selectedRol: idRol, selectedJefe: "" }));
    cargarJefes(idRol);
  };

  const addNewUser = () =>
    enviar(async () => {
      setIntentado(true);
      if (Object.keys(validarUsuario(datos, true)).length > 0) {
        llevarAlPrimerError(formulario);
        return;
      }

      const ok = await escribir(
        () =>
          addUserAsync(token, {
            ...datosParaServidor(datos),
            userName: datos.userName,
            passWord: datos.passWord,
          }),
        "No se pudo agregar al usuario"
      );
      if (!ok) return;
      const nombre = datos.userName;
      setDatos(DATOS_VACIOS);
      cargarJefes("");
      setIntentado(false);
      aviso.exito(`Usuario ${nombre} agregado`);
    });

  return (
    <Container maxWidth={false} disableGutters>
      <PageHeader
        titulo="Nuevo usuario"
        subtitulo="Crea el acceso al sistema y a la app, con su rol y su jefe inmediato"
        icono={<FontAwesomeIcon icon={faUserPlus} />}
        acciones={
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`${ruta}/users`)}
          >
            Regresar
          </Button>
        }
      />

      {error ? (
        <EstadoError
          titulo={
            error === "conexion"
              ? "No se pudo conectar con el servidor"
              : "No se pudo cargar el formulario"
          }
          mensaje="Faltan los roles para crear al usuario. Revisa tu conexión e inténtalo de nuevo."
          onReintentar={getRoles}
        />
      ) : cargando ? (
        <CargandoFormulario etiqueta="Cargando el formulario…" />
      ) : (
        <Stack spacing={3} ref={formulario}>
          <FormularioUsuario
            datos={datos}
            onCampo={onCampo}
            onRol={onRol}
            errores={errores}
            esAlta
            deshabilitado={guardando}
            roles={roles}
            estadoJefes={{ jefes, cargandoJefes, errorJefes }}
          />

          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-end" } }}>
            <Button
              variant="contained"
              size="large"
              disabled={guardando}
              onClick={() => addNewUser()}
              startIcon={guardando ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ width: { xs: "100%", sm: "auto" }, minWidth: 220 }}
            >
              {guardando ? "Guardando…" : "Agregar usuario"}
            </Button>
          </Box>
        </Stack>
      )}
    </Container>
  );
};

export default AddUser;
